import React, { useMemo } from 'react';
import { Row, Col, Card, CardBody } from 'reactstrap';
import AnalyticsSection from '../components/analytics_section';
import OverviewCard from '../components/overview_card';
import BarChartCard from '../components/barchart_card';
import MetricSummaryRow from '../components/metric_summary';
import HorizontalBarChart from '../components/horizontal_barchart';
import { formatNumber, getPublisherConfigInfo } from '../data';
import { useGetViewershipInsightsQuery } from '../../../store/api/analyticsApi';
import { differenceInMonths } from 'date-fns';

const SkeletonBox = ({ width = '100%', height = '16px', style = {} }) => (
  <div
    className="skeleton-box"
    style={{ width, height, ...style }}
  />
);

const TopTitlesSkeleton = () => (
  <div style={{ height: '340px', paddingTop: '8px' }}>
    {[...Array(8)].map((_, index) => (
      <div
        key={index}
        className="d-flex align-items-center mb-3"
        style={{ gap: '12px' }}
      >
        <SkeletonBox width="32px" height="12px" />
        <SkeletonBox
          width={`${55 + ((index % 4) * 10)}%`}
          height="18px"
          style={{ borderRadius: '6px' }}
        />
      </div>
    ))}
  </div>
);

const ViewershipTab = ({
  viewershipDateRange,
  setViewershipDateRange,
  viewedMinutesData,
  viewsData,
  activeUsersData,
  newRegistrationsData,
  topShows,
  totalUsers,
  liveViewers,
  activeUsersStats,
  isLoading = false,
  loadingStates = {},
}) => {
  const isContentPartner = localStorage.getItem('role_id') == 8;
  const {
    totalUsers: totalUsersLoading = isLoading,
    liveViewers: liveViewersLoading = isLoading,
    activeUsers: activeUsersLoading = isLoading,
    newUsers: newUsersLoading = isLoading,
    viewedMinutes: viewedMinutesLoading = isLoading,
    views: viewsLoading = isLoading,
    topShows: topShowsLoading = isLoading,
  } = loadingStates;

  const isMoreThanOneMonth = useMemo(() => {
    if (viewershipDateRange && viewershipDateRange.length > 0) {
      const { startDate, endDate } = viewershipDateRange[0];
      if (startDate && endDate) {
        const start = typeof startDate === 'string' ? new Date(startDate) : startDate;
        const end = typeof endDate === 'string' ? new Date(endDate) : endDate;
        return differenceInMonths(end, start) >= 1;
      }
    }
    return false;
  }, [viewershipDateRange]);

  // Prepare stable date args for the RTK insights query
  const insightArgs = useMemo(() => {
    const pubId = localStorage.getItem('pub_id');
    if (!viewershipDateRange?.[0] || !pubId) return null;
    const { startDate, endDate } = viewershipDateRange[0];
    if (!startDate || !endDate) return null;
    const start = new Date(typeof startDate === 'string' ? startDate : startDate);
    const end = new Date(typeof endDate === 'string' ? endDate : endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return { pubId, startDate: start.toISOString(), endDate: end.toISOString() };
  }, [viewershipDateRange]);

  // RTK Query — replaces the manual useEffect + axios POST
  const { data: insightsData } = useGetViewershipInsightsQuery(
    insightArgs ?? {},
    { skip: !insightArgs || isContentPartner }
  );
  const insights = insightsData?.insights || [];

  const getSignFromPercentage = (percentage) => {
    if (percentage) {
      const str = percentage;
      if (str[0] == "-") {
        return -1;
      } else {
        return 1;
      }
    }
  }

  return (
    <>
      <AnalyticsSection
        title="Quick Overview"
        dateRange={viewershipDateRange}
        onDateChange={setViewershipDateRange}
        reportLink="/new-analytics/viewership"
        isQuickOverview={true}
        hideReportLink={isContentPartner}
      >
        <Row className={`row-cols-1 row-cols-sm-2 row-cols-md-3 
                        ${getPublisherConfigInfo("linear_needed") == "True"
            ? "row-cols-lg-5" : "row-cols-lg-4"}`}>

          {!isContentPartner && <Col className={isContentPartner ? '' : 'border-column'}>
            <OverviewCard
              title="Total Users"
              value={formatNumber(totalUsers)}
              link='/operations/UserManagment'
              showLink={true}
              isLoading={totalUsersLoading}
            />
          </Col>}

          {(getPublisherConfigInfo("linear_needed") == "True") ?
            <Col className={isContentPartner ? '' : 'border-column'}>
              <OverviewCard
                title="Live Viewers"
                value={formatNumber(liveViewers)}
                isLoading={liveViewersLoading}
              />
            </Col>
            : null}

          {!isContentPartner && <Col>
            <OverviewCard
              title="Active Users"
              value={activeUsersStats?.users_count}
              percentageChange={getSignFromPercentage(activeUsersStats?.percentage)}
              formattedPercentageChange={activeUsersStats?.percentage}
              link='/report/total_active_user_report'
              showLink={true}
              isLoading={activeUsersLoading}
            />
          </Col>}

          <Col className={isContentPartner ? '' : 'border-column'}>
            <OverviewCard
              title="Minutes Viewed"
              value={formatNumber(viewedMinutesData.netValue)}
              percentageChange={viewedMinutesData.percentageChange}
              formattedPercentageChange={viewedMinutesData.formattedPercentageChange}
              link="/report/hourly_engagement"
              showLink={true}
              isLoading={viewedMinutesLoading}
            />
          </Col>

          {!isContentPartner && <Col>
            <OverviewCard
              title="New Users"
              value={formatNumber(newRegistrationsData.netValue)}
              percentageChange={newRegistrationsData.percentageChange}
              formattedPercentageChange={newRegistrationsData.formattedPercentageChange}
              link="/report/totaluser_report"
              showLink={true}
              isLoading={newUsersLoading}
            />
          </Col>}
        </Row>
        {!isContentPartner && <MetricSummaryRow
          metrics={
            isMoreThanOneMonth ? [
              {
                label: "active users",
                value: formatNumber(activeUsersData.netValue),
                percentageChange: activeUsersData.percentageChange,
                period: viewershipDateRange[0].label
              },
              {
                label: "minutes viewed",
                value: formatNumber(viewedMinutesData.netValue),
                percentageChange: viewedMinutesData.percentageChange,
                period: viewershipDateRange[0].label
              },
              {
                label: "new users",
                value: formatNumber(newRegistrationsData.netValue),
                percentageChange: newRegistrationsData.percentageChange,
                period: viewershipDateRange[0].label
              }
            ] : insights.map(insight => ({
              isAI: true,
              insight: insight
            }))
          }
        />}
      </AnalyticsSection>

      <AnalyticsSection
        title="Viewership Overview"
        dateRange={viewershipDateRange}
        onDateChange={setViewershipDateRange}
        reportLink="/new-analytics/viewership"
        hideReportLink={isContentPartner}
      >
        <Row>
          {!isContentPartner && (
            <Col md={6} className="mb-3">
              <BarChartCard
                title="Active Users"
                value={activeUsersStats?.users_count}
                percentageChange={getSignFromPercentage(activeUsersStats?.percentage)}
                formattedPercentageChange={activeUsersStats?.percentage}
                xAxis={activeUsersData.xAxis}
                yAxis={activeUsersData.yAxis}
                dataName="Users"
                isLoading={activeUsersLoading}
              />
            </Col>
          )}
          {!isContentPartner && (
            <Col md={6} className="mb-3">
              <BarChartCard
                title="New Users"
                value={formatNumber(newRegistrationsData.netValue)}
                percentageChange={newRegistrationsData.percentageChange}
                formattedPercentageChange={newRegistrationsData.formattedPercentageChange}
                xAxis={newRegistrationsData.xAxis}
                yAxis={newRegistrationsData.yAxis}
                dataName="Users"
                isLoading={newUsersLoading}
              />
            </Col>
          )}
        </Row>
        <Row>
          <Col md={isContentPartner ? 6 : 6} className="mb-3">
            <BarChartCard
              title="Viewed Minutes"
              value={formatNumber(viewedMinutesData.netValue)}
              percentageChange={viewedMinutesData.percentageChange}
              formattedPercentageChange={viewedMinutesData.formattedPercentageChange}
              xAxis={viewedMinutesData.xAxis}
              yAxis={viewedMinutesData.yAxis}
              dataName="Minutes"
              isLoading={viewedMinutesLoading}
            />
          </Col>
          {isContentPartner ? (
            <Col md={6} className="mb-3">
              <BarChartCard
                title="Views"
                value={formatNumber(viewsData.netValue)}
                percentageChange={viewsData.percentageChange}
                formattedPercentageChange={viewsData.formattedPercentageChange}
                xAxis={viewsData.xAxis}
                yAxis={viewsData.yAxis}
                dataName="Views"
                isLoading={viewsLoading}
              />
            </Col>
          ) : (
            <Col md={6} className="mb-3">
              <CardBody className='p-3'>
                <h5>Top Titles</h5>
                {topShowsLoading ? (
                  <TopTitlesSkeleton />
                ) : (
                  <div style={{ height: '340px', overflow: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }} className="hide-scrollbar">
                    <HorizontalBarChart
                      valueLabel="Views"
                      categories={topShows.map((show) => show.name)}
                      values={topShows.map((show) => show.views)}
                    />
                  </div>
                )}
              </CardBody>
            </Col>
          )}
        </Row>
      </AnalyticsSection>
    </>
  );
};

export default ViewershipTab;
